import type { SocketResultData } from '@/components/results/socket/result-socket-models'

/**
 * Interface for tracking socket message entries with timestamps
 */
interface SocketMessageEntry {
  data: SocketResultData<unknown>
  timestamp: number
}

/**
 * Robust socket message deduplication service using Maps for better performance and memory management
 */
export class SocketMessageDeduplicationService {
  private socketsByKey = new Map<string, SocketMessageEntry>()
  private socketsByContentHash = new Map<string, SocketMessageEntry>()
  private readonly maxSize: number
  private readonly cleanupThreshold: number

  constructor(maxSize: number = 200) {
    this.maxSize = maxSize
    this.cleanupThreshold = maxSize * 2 // Trigger cleanup when we exceed 2x the target size
  }

  /**
   * Creates a hash of the socket message content for deduplication
   */
  private createMessageContentHash(socketResultData: SocketResultData<unknown>): string {
    // Create a simplified object with the key fields for comparison
    const contentForHash = {
      action: socketResultData.action,
      comp: socketResultData.comp,
      payload: socketResultData.payload
    }

    // Simple hash function - you could use a more sophisticated one if needed
    return btoa(JSON.stringify(contentForHash)).replace(/[^a-zA-Z0-9]/g, '')
  }

  /**
   * Check if a message is a duplicate
   */
  isDuplicate(socketResultData: SocketResultData<unknown>): boolean {
    // Check key-based deduplication first (faster)
    if (this.socketsByKey.has(socketResultData.key)) {
      console.log('Duplicate key detected:', socketResultData.key)
      return true
    }

    // Check content-based deduplication
    const messageContentHash = this.createMessageContentHash(socketResultData)
    if (this.socketsByContentHash.has(messageContentHash)) {
      console.log('Duplicate content detected:', messageContentHash)
      return true
    }

    return false
  }

  /**
   * Add a message to the deduplication maps
   */
  addMessage(socketResultData: SocketResultData<unknown>): void {
    const timestamp = Date.now()
    const entry: SocketMessageEntry = {
      data: socketResultData,
      timestamp
    }

    // Store by key
    this.socketsByKey.set(socketResultData.key, entry)

    // Store by content hash
    const messageContentHash = this.createMessageContentHash(socketResultData)
    this.socketsByContentHash.set(messageContentHash, entry)

    // Trigger cleanup if needed
    this.cleanupIfNeeded()
  }

  /**
   * Clean up old entries using LRU strategy
   */
  private cleanupIfNeeded(): void {
    if (this.socketsByContentHash.size <= this.cleanupThreshold) {
      return
    }

    console.log(`Starting cleanup: ${this.socketsByContentHash.size} entries, target: ${this.maxSize}`)

    // Convert to array and sort by timestamp (oldest first)
    const entriesByTimestamp = Array.from(this.socketsByContentHash.entries())
      .map(([hash, entry]) => ({ hash, entry }))
      .sort((a, b) => a.entry.timestamp - b.entry.timestamp)

    // Calculate how many to remove
    const entriesToRemove = this.socketsByContentHash.size - this.maxSize
    const toRemove = entriesByTimestamp.slice(0, entriesToRemove)

    // Remove from both maps
    toRemove.forEach(({ hash, entry }) => {
      this.socketsByContentHash.delete(hash)
      this.socketsByKey.delete(entry.data.key)
    })

    console.log(`Cleaned up ${toRemove.length} old entries. New size: ${this.socketsByContentHash.size}`)
  }

  /**
   * Get current statistics
   */
  getStats(): { keyMapSize: number; contentHashMapSize: number; maxSize: number } {
    return {
      keyMapSize: this.socketsByKey.size,
      contentHashMapSize: this.socketsByContentHash.size,
      maxSize: this.maxSize
    }
  }

  /**
   * Clear all entries (useful for testing or reset)
   */
  clear(): void {
    this.socketsByKey.clear()
    this.socketsByContentHash.clear()
  }
}
